import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { DocumentTextIcon } from '../../../components/ui/Icons';

const ExclamationIcon: React.FC<{ className?: string }> = ({ className = 'h-5 w-5' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
    />
  </svg>
);

type Props = {
  /** new requisition (no case) with importFromDelivery, etc. */
  requisitionTo: string;
};

const SpareDeliveryShortfallAlert: React.FC<Props> = ({ requisitionTo }) => {
  const { colors } = useTheme();

  return (
    <div
      role="status"
      className={cn(
        'flex flex-col gap-3 rounded-xl border px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5 sm:py-3.5',
        'border-amber-200/90 bg-amber-50/90 dark:border-amber-800/50 dark:bg-amber-950/30',
        colors.text,
      )}
    >
      <div className="flex min-w-0 gap-3">
        <ExclamationIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-700 dark:text-amber-400" />
        <div>
          <p className="text-sm font-semibold text-amber-950 dark:text-amber-100">Delivery not complete</p>
          <p className={cn('mt-0.5 text-sm text-amber-900/90 dark:text-amber-200/90')}>
            Not all line items are fully delivered, or received quantity does not match the purchase order. Create
            a requisition (no case) to cover shortfall lines, importing this delivery.
          </p>
        </div>
      </div>
      <Link
        to={requisitionTo}
        className={cn(
          'inline-flex h-9 shrink-0 items-center justify-center gap-1.5 self-start rounded-lg border border-amber-200/80 bg-amber-50/50 px-3 text-sm font-semibold text-amber-950 shadow-sm transition-all hover:bg-amber-100 dark:border-amber-800/60 dark:bg-amber-950/30 dark:text-amber-100 dark:hover:bg-amber-900/30',
          'sm:self-center',
        )}
        title="Create a requisition with no case, importing shortfall / pending lines from this delivery"
      >
        <DocumentTextIcon className="h-4 w-4 shrink-0" />
        New requisition (no case)
      </Link>
    </div>
  );
};

export default SpareDeliveryShortfallAlert;
